import { createSlice } from '@reduxjs/toolkit';
import {
    ACCOUNTING_AREA_ID_MARKER,
    processRequestForAccountingArea,
    processRequestForAccountingAreaWithReplacement,
} from './network/managedRequestForAccountingArea';
import { API } from '../constants';
import { PbRequest } from './pbRequest';
import { PbDispatch } from './index';
import { pbRequestReset } from './pbRequestSlice';

enum LifecycleState {
    IDLE = 'IDLE',
    READY_FOR_USE = 'READY_FOR_USE',
    ACTIVE = 'ACTIVE',
    COMPLETE = 'COMPLETE',
    POWER_OFF = 'POWER_OFF',
    RESERVED = 'RESERVED',
}

export enum ApplianceType {
    WASHING_MACHINE = 'WASHING_MACHINE',
    DRYER = 'DRYER',
}

enum ControlType {
    POWER = 'POWER',
    POWER_CONSTANT = 'POWER_CONSTANT',
    PAYMENT_SIGNAL = 'PAYMENT_SIGNAL',
}

enum ContractualType {
    Admin = 'Admin',
    WWB = 'WWB',
    WWC = 'WWC',
    WWC_IDOS = 'WWC_IDOS',
    RMS = 'RMS',
    CITY_POP = 'CITY_POP',
    WHITE_LABEL_WWC = 'WHITE_LABEL_WWC',
    WHITE_LABEL_WWB = 'WHITE_LABEL_WWB',
    WWC_MINIMUM_USAGE = 'WWC_MINIMUM_USAGE',
    WWC_IDOS_MINIMUM_USAGE = 'WWC_IDOS_MINIMUM_USAGE',
}

enum InstallationVerificationStatus {
    IN_PREPARATION = 'IN_PREPARATION',
    LOADING_PHASE = 'LOADING_PHASE',
    LOADING_PHASE_CANCEL_REQUESTED = 'LOADING_PHASE_CANCEL_REQUESTED',
    APPLIANCE_RUNNING = 'APPLIANCE_RUNNING',
    PICKUP_PHASE = 'PICKUP_PHASE',
    CANCELED = 'CANCELED',
    SUCCESS = 'SUCCESS',
    FAILURE = 'FAILURE',
    EXPIRED = 'EXPIRED',
}

enum OperationalState {
    OPERATIONAL = 'OPERATIONAL',
    INSTALLATION_VERIFICATION = 'INSTALLATION_VERIFICATION',
    BLIND_MAINTENANCE = 'BLIND_MAINTENANCE',
    CLIENT_INSTALLATION = 'CLIENT_INSTALLATION',
    NON_OPERATIONAL = 'NON_OPERATIONAL',
    NOT_CONFIGURED = 'NOT_CONFIGURED',
}

// Backend error tags
export const INSTALLATION_VERIFICATION_NOT_FINISHED =
    'INSTALLATION_VERIFICATION_NOT_FINISHED';
export const INSTALLATION_VERIFICATION_WRONG_OPERATIONAL_STATE =
    'INSTALLATION_VERIFICATION_WRONG_OPERATIONAL_STATE';
export const INSTALLATION_VERIFICATION_NOT_FOUND =
    'INSTALLATION_VERIFICATION_NOT_FOUND';
export const INSTALLATION_VERIFICATION_CYCLE_ALREADY_STARTED =
    'INSTALLATION_VERIFICATION_CYCLE_ALREADY_STARTED';

export interface Appliance {
    id?: number;
    state: LifecycleState;
    appliance_type: ApplianceType;
    location_id: number;
    local_name: string;
    short_name: string;
    control_type: ControlType;
    product_code: string;
    manufacturing_batch?: string;
    model_group?: string;
    serial_number: string;
    enabled?: boolean;
    contractual_type: ContractualType;
    operational_state?: OperationalState;
    unfinished_installation_verification?: boolean;
    installation_verification_status?: InstallationVerificationStatus;
    installation_verification_request_time?: number;
}

export type LaundryRoomToApplianceIdToAppliance = {
    [laundryRoomId: number]: { [applianceId: number]: Appliance };
};

interface ApplianceStore {
    appliancesByLaundryRoom: LaundryRoomToApplianceIdToAppliance;
}

const initialState: ApplianceStore = {
    appliancesByLaundryRoom: {},
};

export const applianceSlice = createSlice({
    name: 'appliance',
    initialState,
    reducers: {
        rememberAppliancesForLaundryRoom(state, action) {
            const laundryRoomId = action.payload.location_id;
            const appliances: Appliance[] = action.payload.data;
            const byApplianceId: { [key: number]: Appliance } = {};
            appliances.forEach((app) => (byApplianceId[app.id] = app));
            state.appliancesByLaundryRoom[laundryRoomId] = byApplianceId;
        },
        rememberSingleAppliance(state, action) {
            const appliance: Appliance = action.payload;
            if (!appliance.id || !appliance.location_id) {
                return;
            }
            const byApplianceId = {
                ...state.appliancesByLaundryRoom[appliance.location_id],
            };
            byApplianceId[appliance.id] = {
                ...byApplianceId[appliance.id],
                ...appliance,
            };
            const newList = { ...state.appliancesByLaundryRoom };
            newList[appliance.location_id] = byApplianceId;
            state.appliancesByLaundryRoom = newList;
        },
        forgetSingleAppliance(state, action) {
            const locationId = action.payload.location_id;
            const applianceId = action.payload.appliance_id;
            if (!locationId || !applianceId) {
                return;
            }
            const byApplianceId = {
                ...state.appliancesByLaundryRoom[locationId],
            };
            delete byApplianceId[applianceId];
            const newList = { ...state.appliancesByLaundryRoom };
            newList[locationId] = byApplianceId;
            state.appliancesByLaundryRoom = newList;
        },
    },
});

export const {
    rememberAppliancesForLaundryRoom,
    rememberSingleAppliance,
    forgetSingleAppliance,
} = applianceSlice.actions;

export function resetApplianceRequestStates() {
    return async (dispatch: PbDispatch) => {
        dispatch(pbRequestReset(PbRequest.CREATE_APPLIANCE));
        dispatch(pbRequestReset(PbRequest.UPDATE_APPLIANCE));
        dispatch(pbRequestReset(PbRequest.DELETE_APPLIANCE));
        dispatch(pbRequestReset(PbRequest.START_INSTALLATION_VERIFICATION));
        dispatch(pbRequestReset(PbRequest.CANCEL_INSTALLATION_VERIFICATION));
    };
}

export function LoadAppliancesByLocation(location_id: number) {
    return async (dispatch: PbDispatch) => {
        const request = { method: 'GET' };
        const res = await processRequestForAccountingArea(
            dispatch,
            `${API}/locations/${location_id}/appliances`,
            request,
            PbRequest.FETCH_APPLIANCES
        );
        dispatch(rememberAppliancesForLaundryRoom({ location_id, data: res }));
        return res;
    };
}

export function DeleteAppliance(
    appliance_id: number,
    location_id: number,
    onSuccess?: () => void
) {
    return async (dispatch: PbDispatch) => {
        try {
            const request = { method: 'DELETE' };
            await processRequestForAccountingArea(
                dispatch,
                `${API}/appliances/${appliance_id}`,
                request,
                PbRequest.DELETE_APPLIANCE
            );
            dispatch(forgetSingleAppliance({ appliance_id, location_id }));
            onSuccess?.();
        } catch {}
    };
}

export function UpdateAppliance(appliance: Appliance, onSuccess?: () => void) {
    return async (dispatch: PbDispatch) => {
        try {
            const request = {
                method: 'POST',
                body: JSON.stringify(appliance),
            };
            await processRequestForAccountingArea(
                dispatch,
                `${API}/appliances/${appliance.id}`,
                request,
                PbRequest.UPDATE_APPLIANCE
            );
            dispatch(rememberSingleAppliance(appliance));
            onSuccess?.();
        } catch {}
    };
}

export function CreateAppliance(appliance: Appliance, onSuccess?: () => void) {
    return async (dispatch: PbDispatch) => {
        try {
            const request = {
                method: 'POST',
                body: JSON.stringify(appliance),
            };
            const applianceId = await processRequestForAccountingArea<number>(
                dispatch,
                `${API}/appliances`,
                request,
                PbRequest.CREATE_APPLIANCE
            );

            const extendedAppliance: Appliance = {
                ...appliance,
                id: applianceId,
            };
            dispatch(rememberSingleAppliance(extendedAppliance));
            onSuccess?.();
        } catch {}
    };
}

export function StartInstallationVerification(
    applianceId: number,
    onSuccess?: () => void
) {
    return async (dispatch: PbDispatch) => {
        try {
            const request = { method: 'POST' };
            await processRequestForAccountingAreaWithReplacement(
                dispatch,
                `${API}/accounting_area/${ACCOUNTING_AREA_ID_MARKER}/appliances/${applianceId}/verify_installation`,
                request,
                PbRequest.START_INSTALLATION_VERIFICATION
            );
            onSuccess?.();
        } catch {}
    };
}

export function CancelInstallationVerification(
    applianceId: number,
    onSuccess?: () => void
) {
    return async (dispatch: PbDispatch) => {
        try {
            const request = { method: 'POST' };
            await processRequestForAccountingAreaWithReplacement(
                dispatch,
                `${API}/accounting_area/${ACCOUNTING_AREA_ID_MARKER}/appliances/${applianceId}/cancel_installation_verification`,
                request,
                PbRequest.CANCEL_INSTALLATION_VERIFICATION
            );
            onSuccess?.();
        } catch {}
    };
}

export default applianceSlice.reducer;
